import { html, css, nothing } from 'lit';
import { when } from 'lit/directives/when.js';
import { customElement, query, state } from 'lit/decorators.js';
import { consume } from '@lit/context';
import { ltnToastServiceContext, nsLoaderContext, nsSettingsServiceContext } from '../../../../ns-services/contexts.js';
import type { NSLoader } from '../../../../ns-services/ns-loader-service.js';
import { runBehindShutter } from '../../../../helpers/reload-behind-shutter.js';

import { nsLayoutStyles } from '../../../../ns-styles/index.js';
import { LtnToastService } from '../../../../ltn-toast/ltn-toast-service.js';

import { NSSettingsService } from '../../../../ns-settings/ns-settings-service.js';

import { FinalFormController, FormConfig } from '../../../../ns-form/final-form-controller.js';

import JobSchema from '../../../../models/job-schema.js';

import { callLambda, getActionFromLambdaResults } from '../../../../helpers/lambda.js';

import NSJobComponent from '../../job.js';

import Company from '../../../../models/company.js';

import '../../../../ns-elements/ns-tiles-options.js';
import '../../../../ns-elements/ns-advanced-search.js';
import '../../../../ns-elements/ns-summary-card.js';
import '@awesome.me/webawesome/dist/components/select/select.js';
import '@awesome.me/webawesome/dist/components/option/option.js';
import '../../../../ns-elements/ns-upload.js';
import '../../../../ns-elements/ns-input-field.js';

import { getDeepValue, renderFormErrorSpan } from '../../../../helpers/dom.js';
import { NSTilesOptions } from '../../../../ns-elements/ns-tiles-options.js';
import { NSAdvancedSearch } from '../../../../ns-elements/ns-advanced-search.js';
import { NSUpload } from '../../../../ns-elements/ns-upload.js';
import '../../../../ns-elements/ns-job-layout.js';
import { localCalendarDate } from '../../../../helpers/format.js';
import '@awesome.me/webawesome/dist/components/button/button.js';
import definition from '@job-schemas/company/find-or-create.json';
import { jobSchema } from '../../job-schema.js';

interface FormValues {
  name: string;
  type: 'PUBLIC_LIMITED_COMPANY' | 'PRIVATE_LIMITED_COMPANY' | 'PRIVATE_UNLIMITED_COMPANY' | 'INTERNATIONAL_COMPANY';
  companiesHouseNumber: number;

  address1: string;
  address2: string;
  city: string;
  county: string;
  postcode: string;
  country: string;

  incorporationDay: string;
  incorporationMonth: string;
  incorporationYear: string;
}

@customElement(NSCompanyFindOrCreate.is)
export default class NSCompanyFindOrCreate extends NSJobComponent {
  static get is() {
    return 'ns-company-find-or-create';
  }

  // Closes over the page before it reloads into the new company
  @consume({ context: nsLoaderContext })
  private __loader?: NSLoader;

  @consume({ context: nsSettingsServiceContext })
  private __settings?: NSSettingsService;

  @consume({ context: ltnToastServiceContext })
  private __toast?: LtnToastService;

  @query('ns-tiles-options')
  private __nsTilesOptions?: NSTilesOptions;

  @query('#advanced-company-search')
  private __advancedSearch?: NSAdvancedSearch;

  @query('#upload-company-logo')
  private __uploadCompanyLogo?: NSUpload;

  @state()
  private __selectedOrganisationType?: string;

  @state()
  private __companyNumber?: string;

  @state()
  private __searchResults: Company[] = [];

  @state()
  private __activeSearch: boolean = false;

  @state()
  private __createdCompany?: {
    name?: string;
    profile?: string;
    type?: string;
    companiesHouseNumber?: number;
    address1?: string;
    address2?: string;
    city?: string;
    postcode?: string;
    county?: string;
    country?: string;
    incorporationDate?: string;
  };

  private __companyDetails?: {
    label: string;
    prop: string;
  }[];

  @state()
  private __errors: Partial<Record<keyof FormValues, string>> = {};

  private __changeResultsListener = (ev: Event) => {
    const event = ev as CustomEvent;
    const { results, isValidValue } = event.detail;

    this.__searchResults = results;
    if (!isValidValue) {
      this.__activeSearch = false;
      return;
    }

    this.__activeSearch = true;
    this.__updateCompanyLogoElement();
  };

  private __selectItemListener = (ev: Event) => {
    const customEvent = ev as CustomEvent;
    const item = customEvent.detail;

    if (!item) return;
    this.__createdCompany = {
      name: item.title,
      type: item.company_type,
      companiesHouseNumber: item.company_number,
      address1: item.address.address_line_1,
      address2: item.address.address_line_2,
      city: item.address.locality,
      postcode: item.address.postal_code,
      county: item.address.region,
      country: item.address.country,
      incorporationDate: item.date_of_creation,
    };
    this.__confirmCompletion();
  };

  private __companyTypes = [
    { value: 'PUBLIC_LIMITED_COMPANY', label: 'Public Limited Company' },
    { value: 'PRIVATE_LIMITED_COMPANY', label: 'Private Limited Company' },
    { value: 'PRIVATE_UNLIMITED_COMPANY', label: 'Private Unlimited Company' },
    { value: 'INTERNATIONAL_COMPANY', label: 'International Company' },
  ];

  private __createFormController: FinalFormController<FormValues>;

  static get schema(): JobSchema {
    return jobSchema(definition, { outcomes: { join: { type: 'hidden' }, created: { type: 'hidden' } } });
  }

  static styles = [
    nsLayoutStyles,
    ...super.styles,
    css`
      .field + .field {
        margin-top: var(--wa-space-m);
      }
    `,
  ];

  constructor() {
    super();

    this.__createFormController = new FinalFormController(this, {
      onSubmit: async (values) => await this.__createCompanyFormSubmit(values),
    } as FormConfig<FormValues>);
  }

  async connectedCallback() {
    await super.connectedCallback();

    if (!this._db) throw new Error('Unable to find db service');

    if (!this._auth) throw new Error('Unable to find auth service');

    if (!this.__settings) throw new Error('Unable to find settings service');

    if (!this._directory) throw new Error('Unable to find NSWorkflowService');

    if (!this.__toast) throw new Error('Unable to find toast service');

    const invitationId = this._job?.inputValues?.invitationId as string;
    if (!invitationId) throw new Error('Missing required job invitation ID input value');
    const invitation = await this._db.getById('invitation', invitationId);
    if (!invitation) throw new Error(`Could not find an invitation with ID: ${invitationId}`);
    this.__companyNumber = invitation.invitee?.company?.registrarIdentifier;

    this.__companyDetails = [
      {
        label: 'Registered Name',
        prop: 'name',
      },
      {
        label: 'Company Type',
        prop: 'type',
      },
      {
        label: 'Registered No.',
        prop: 'companiesHouseNumber',
      },
      {
        label: 'Address 1',
        prop: 'address1',
      },
      {
        label: 'Address 2',
        prop: 'address2',
      },
      {
        label: 'City',
        prop: 'city',
      },
      {
        label: 'Postcode',
        prop: 'postcode',
      },
      {
        label: 'County',
        prop: 'county',
      },
      {
        label: 'Country',
        prop: 'country',
      },
      {
        label: 'Incorporation Date',
        prop: 'incorporationDate',
      },
    ];

    const metadata = this.getMetadataValue('CREATED_COMPANY');
    if (metadata) {
      this.__createdCompany = JSON.parse(metadata);
      this.__confirmCompletion();
    }

    this.__nsTilesOptions?.init(
      [
        {
          id: 'companies-house',
          iconPath: '/images/icons/companies-house-search.svg',
          title: 'Limited Company',
          description: 'Private Ltd (Ltd) or Public Limited Company (PLC) registered at Companies House',
        },
        {
          id: 'public-sector-body',
          iconPath: '/images/icons/public-sector.svg',
          title: 'Public Sector Body',
          description: 'NHS, Local Authority, Central Government, Schools, or other public body',
          disabled: true,
        },
        {
          id: 'sole-trader',
          iconPath: '/images/icons/sole-trader.svg',
          title: 'Sole Trader',
          description: 'Self employed individual trading under your own name or a business name',
          disabled: true,
        },
        {
          id: 'charity-third-sector',
          iconPath: '/images/icons/charity.svg',
          title: 'Charity / Third Sector',
          description: 'Registered charity, CIC, or voluntary organisation not on Companies House',
          disabled: true,
        },
        {
          id: 'other-unregistered-entity',
          iconPath: '/images/icons/unregistered-organisation.svg',
          title: 'Other / Unregistered Entity',
          description: 'Partnerships, foreign entities, consortia, or organisations not covered above',
          disabled: true,
        },
      ],
      'Select your organisation type',
    );

    this.__nsTilesOptions?.addEventListener('selected', (ev: Event) => {
      if (!this._job) throw new Error('Missing required job');
      this.clearError();
      const event = ev as CustomEvent;
      const id = event.detail;
      if (id !== 'COMPANIES_HOUSE') {
        this.showError('Only Limited Company option is implemented');
        return;
      }

      this.__selectedOrganisationType = id;
      this.__updateSearch();
    });
  }

  async disconnectedCallback() {
    super.disconnectedCallback();

    this.__advancedSearch?.removeEventListener('change-results', this.__changeResultsListener);
    this.__advancedSearch?.removeEventListener('select-item', this.__selectItemListener);
  }

  private async __updateSearch() {
    await this.updateComplete;

    if (!this.__advancedSearch) throw new Error('Could find advanced search element');
    if (!this.__selectedOrganisationType) throw new Error('Missing required selected organisation type');

    // TODO refactor this when you add the buttress instance that will hold the registry data
    const details = [
      {
        label: 'Registered Name',
        prop: 'companiesRegistrar.companyName',
      },
      {
        label: 'Address 1',
        prop: 'companiesRegistrar.registeredAddress.address1',
      },
      {
        label: 'Address 2',
        prop: 'companiesRegistrar.registeredAddress.address2',
      },
      {
        label: 'City',
        prop: 'companiesRegistrar.registeredAddress.city',
      },
      {
        label: 'Postcode',
        prop: 'companiesRegistrar.registeredAddress.postcode',
      },
      {
        label: 'County',
        prop: 'companiesRegistrar.registeredAddress.county',
      },
      {
        label: 'Country',
        prop: 'companiesRegistrar.registeredAddress.country',
      },
      {
        label: 'Registered No.',
        prop: 'companiesRegistrar.id',
      },
      {
        label: 'Registered Name',
        prop: 'title',
      },
      {
        label: 'Registered No.',
        prop: 'company_number',
      },
      {
        label: 'Address 1',
        prop: 'address.address_line_1',
      },
      {
        label: 'Address 2',
        prop: 'address.address_line_2',
      },
      {
        label: 'City',
        prop: 'address.locality',
      },
      {
        label: 'Postcode',
        prop: 'address.postal_code',
      },
      {
        label: 'County',
        prop: 'address.region',
      },
      {
        label: 'Country',
        prop: 'address.country',
      },
      {
        label: 'Creation Date',
        prop: 'date_of_creation',
      },
    ];

    this.__advancedSearch.init(details, 'COMPANIES_HOUSE');
    this.__advancedSearch.addEventListener('change-results', this.__changeResultsListener);
    this.__advancedSearch.addEventListener('select-item', this.__selectItemListener);
  }

  private async __updateCompanyLogoElement() {
    await this.updateComplete;
    if (this.__searchResults && this.__searchResults.length > 0) return;

    if (!this.__uploadCompanyLogo) throw new Error('Could not find upload company logo element');
    this.__uploadCompanyLogo.init(
      ['image/png', 'image/jpeg'],
      "Company logo's should be 300px x 300px and be in .jpg or .png format.",
    );
  }

  private __confirmCompletion() {
    this.confirmCardCompletion();
  }

  private async __createCompanyFormSubmit(value: Partial<FormValues>) {
    if (!this._db) throw new Error('Missing db service');
    if (!this._auth) throw new Error('Missing auth service');
    if (!this._directory) throw new Error('Missing dir service');

    const errors: Partial<Record<keyof FormValues, string>> = {};
    const name = value.name?.trim();
    if (!name) errors.name = 'Name is required';

    if (!value.type) errors.type = 'Company type is required';

    if (!value.companiesHouseNumber) errors.companiesHouseNumber = 'Companies house number is required';

    const address1 = value.address1?.trim();
    if (!address1) errors.address1 = 'Address line 1 is required';

    const address2 = value.address2?.trim();

    const city = value.city?.trim();
    if (!city) errors.city = 'City is required';

    const postcode = value.postcode?.trim();
    if (!postcode) errors.postcode = 'Postcode is required';

    const country = value.country?.trim();
    if (!country) errors.country = 'Country is required';

    let incorporationDate = undefined;
    if (value.incorporationDay && value.incorporationMonth && value.incorporationYear) {
      incorporationDate = localCalendarDate(
        Number(value.incorporationYear),
        Number(value.incorporationMonth),
        Number(value.incorporationDay),
      );
    }

    if (!incorporationDate) errors.incorporationDay = 'Incorporation date is required';

    this.__errors = errors;
    if (Object.keys(errors).length > 0) return;

    this.__createdCompany = {
      name,
      type: value.type,
      companiesHouseNumber: value.companiesHouseNumber,
      address1,
      address2: address2 || undefined,
      city,
      county: value.county?.trim() || undefined,
      postcode,
      country,
      incorporationDate: incorporationDate,
    };

    await this.updateMetadata('CREATED_COMPANY', JSON.stringify(this.__createdCompany));
    this.__confirmCompletion();
  }

  private async __completeJob() {
    if (!this._db) throw new Error('Missing db service');
    if (!this._auth) throw new Error('Missing auth service');
    if (!this._directory) throw new Error('Missing dir service');
    if (!this.__createdCompany) throw new Error('Missing required created company');

    // TODO: Validate & sanaize inputs
    // Making sure that all required values have been filled
    const name = this.__createdCompany.name;
    if (!name) throw new Error('Unable to create company with no name');
    if (!this.__createdCompany.type) throw new Error('Unable to create company with no type');
    if (!this.__createdCompany.companiesHouseNumber) throw new Error('Unable to create company with no company number');

    if (!this.__createdCompany.address1) throw new Error('Unable to create company with no address1');
    if (!this.__createdCompany.city) throw new Error('Unable to create company with no city');
    if (!this.__createdCompany.postcode) throw new Error('Unable to create company with no postcode');
    if (!this.__createdCompany.county && !this.__createdCompany.country)
      throw new Error('Unable to create company with no county or country');
    if (!this.__createdCompany.incorporationDate)
      throw new Error('Unable to create company with no incorporation date');

    // TODO possibly needs more consideration
    this.__createdCompany.profile = this.__createdCompany.type.includes('PUBLIC') ? 'PUBLIC' : 'PRIVATE';

    const db = this._db;
    const directory = this._directory;
    const body = JSON.stringify({
      invitationId: this.job?.inputValues?.invitationId,
      jobId: this.job.id,
      name: name,
      ...this.__createdCompany,
      incorporatedOn: new Date(this.__createdCompany.incorporationDate).toISOString(),
    });
    const token = this._auth.token;

    try {
      // The shutter closes first; creating the company, completing this card and switching to it happen behind it, then
      // the app reloads, since crag's store still holds the previous company's entities, some under another policy's
      // projection
      await runBehindShutter(this.__loader, `Setting up ${name}…`, async () => {
        const res = await callLambda('company/create', 'POST', token, body);

        const action = await getActionFromLambdaResults(res, 'company_created');
        if (typeof action === 'string') throw new Error('Unexpected action type');

        const company = await db.getById('company', action.id);
        if (!company) throw new Error(`Failed to get company ${action.id}`);

        await this.outcome('created');
        await directory.selectCompanyProfile(company.id);
      });
    } catch (err: unknown) {
      if (err instanceof Error) {
        this.fatalError(err.message);
      } else {
        this.fatalError('Something has gone wrong whilst creating a company');
        throw new Error('company/create lambda throw unexpected error type');
      }
    }
  }

  private renderCreationForm() {
    const { form, register } = this.__createFormController;
    return html`
      <form
        id="create-company-form"
        @submit=${(e: CustomEvent) => {
          e.preventDefault();
          form.submit();
        }}
      >
        <div class="field layout vertical">
          <ns-input-field
            stacked
            required
            ${register('name')}
            placeholder="Company Name"
            label="Registered Company Name*"
          ></ns-input-field>
          ${renderFormErrorSpan('name', this.__errors)}
        </div>

        <div class="field layout vertical">
          <ns-input-field
            stacked
            required
            ${register('address1')}
            placeholder="Address 1"
            label="Address Line 1*"
          ></ns-input-field>
          ${renderFormErrorSpan('address1', this.__errors)}
        </div>

        <div class="field layout vertical">
          <ns-input-field
            stacked
            ${register('address2')}
            placeholder="Address 2"
            label="Address Line 2"
          ></ns-input-field>
        </div>

        <div class="field layout vertical">
          <ns-input-field stacked required ${register('city')} placeholder="City" label="City*"></ns-input-field>
          ${renderFormErrorSpan('city', this.__errors)}
        </div>

        <div class="field layout vertical">
          <ns-input-field stacked ${register('county')} placeholder="County" label="County"></ns-input-field>
          ${renderFormErrorSpan('county', this.__errors)}
        </div>

        <div class="field layout vertical">
          <ns-input-field
            stacked
            required
            ${register('postcode')}
            placeholder="Postcode"
            label="Postcode*"
          ></ns-input-field>
          ${renderFormErrorSpan('postcode', this.__errors)}
        </div>

        <div class="field layout vertical">
          <ns-input-field
            stacked
            required
            ${register('country')}
            placeholder="Country"
            label="Country*"
          ></ns-input-field>
          ${renderFormErrorSpan('country', this.__errors)}
        </div>

        <div class="field layout vertical">
          <ns-input-field
            stacked
            required
            ${register('companiesHouseNumber')}
            placeholder="Company No."
            label="Registered Company No*"
          ></ns-input-field>
          ${renderFormErrorSpan('companiesHouseNumber', this.__errors)}
        </div>

        <div class="field layout vertical">
          <div class="label small mb-1" aria-hidden="true">Company Type*</div>
          <wa-select ${register('type')} label="Company Type" placeholder="Select Company Type">
            ${this.__companyTypes.map(
              (companyType) => html`
                <wa-option value=${companyType.value}>${companyType.label}</wa-option>
              `,
            )}
          </wa-select>
          ${renderFormErrorSpan('type', this.__errors)}
        </div>

        <div class="field layout vertical">
          <fieldset class="fieldset">
            <legend class="label small mb-1">Company Incorporation Date*</legend>
            <div class="layout horizontal center-center">
              <ns-input-field
                required
                ${register('incorporationDay')}
                class="incorporation-day"
                width="45"
                type="number"
                limit="0,31"
                name="incorporationDay"
                placeholder="DD"
                animationType="NONE"
              ></ns-input-field>
              <ns-input-field
                required
                ${register('incorporationMonth')}
                class="incorporation-month"
                width="45"
                type="number"
                limit="0,12"
                name="incorporationMonth"
                placeholder="MM"
                animationType="NONE"
              ></ns-input-field>
              <ns-input-field
                required
                animationType="SLIDE_REVEAL"
                ${register('incorporationYear')}
                class="incorporation-year"
                width="60"
                type="number"
                limit="1,9999"
                name="incorporationYear"
                placeholder="YYYY"
              ></ns-input-field>
              <div class="flex"></div>
              <div class="flex"></div>
            </div>
            ${renderFormErrorSpan('incorporationDay', this.__errors)}
          </fieldset>
        </div>

        <div class="field layout vertical start">
          <div class="label small mb-1" aria-hidden="true">Company Logo</div>
          <ns-upload id="upload-company-logo" label="Company logo"></ns-upload>
        </div>
      </form>
    `;
  }

  private renderCreationStage() {
    return html`
      <div class="job-panel">
        <h3>Create your company</h3>
        <p>Create your company profile inside nodeStream so that you can start collaborating with other businesses.</p>
        ${when(
          this.__createdCompany,
          () => html`
            <ns-summary-card
              id="created-company"
              heading=${this.__createdCompany?.name ?? ''}
              .rows=${(this.__companyDetails ?? []).map((row) => ({
                label: row.label,
                value: getDeepValue(this.__createdCompany, row.prop),
              }))}
              confirmed
            ></ns-summary-card>
          `,
          () => this.renderCreationForm(),
        )}
      </div>
    `;
  }

  private __renderSearchForm() {
    return html`
      <div class="job-panel">
        <h3>Search for your company</h3>
        <p>Let's start by seeing if your company already exists in public registrar</p>
        <ns-advanced-search id="advanced-company-search" value="${this.__companyNumber}"></ns-advanced-search>
      </div>
    `;
  }

  private __renderSelectOrganisationType() {
    return html`
      <div class="job-panel">
        <h3>Select your organisation type</h3>
        <ns-tiles-options></ns-tiles-options>
      </div>
    `;
  }

  renderActions() {
    if (!this.__selectedOrganisationType) {
      return html`
        <wa-button variant="brand" type="button" @click=${() => this.__nsTilesOptions?.confirmSelection()}>
          Confirm
        </wa-button>
      `;
    }
    if (this.__createdCompany) {
      return html`
        <wa-button variant="brand" type="button" @click=${() => this.__completeJob()}>Complete Job</wa-button>
      `;
    }
    if (this.__showCreationStage) {
      const { form } = this.__createFormController;
      return html`
        <wa-button variant="brand" type="button" ?disabled=${form.getState().submitting} @click=${() => form.submit()}>
          Confirm
        </wa-button>
      `;
    }
    return nothing;
  }

  private get __showCreationStage() {
    return (
      !!this.__createdCompany || ((!this.__searchResults || this.__searchResults.length < 1) && this.__activeSearch)
    );
  }

  render() {
    return html`
      <ns-job-layout>
        ${when(
          this.__selectedOrganisationType,
          () => html`
            ${when(!this.__createdCompany, () => this.__renderSearchForm())}
            ${when(this.__showCreationStage, () => this.renderCreationStage())}
          `,
          () => this.__renderSelectOrganisationType(),
        )}
        <div slot="aside" class="primary-info">
          <span class="info-icon" aria-hidden="true">i</span>
          <p>
            Each pathway has different verification requirements. Your identifier will be validated against the
            appropriate official registry in real time.
          </p>
        </div>
      </ns-job-layout>
    `;
  }
}
